import { useCallback, useMemo, useState, useEffect } from "react";
import { useAppSelector } from "@/app/appStore";
import {
  useGetJourneyPredictionCartQuery,
  JourneyPredictionPayload,
  type JourneyPredictionCartResponse,
} from "@/shared/api/lod-api";
import {
  createJourneyPredictionPayload,
  formatTimeDifference,
} from "@/features/annotation/lib/helpers";
import { ANNOTATION_RECORD_STATUSES } from "@/entities/annotation/model/consts";
import { toast } from "react-hot-toast";
import {
  selectEvents,
  selectJourneys,
  selectSelectedOrder,
} from "@/entities/annotation/model/slices/slice";

interface UsePredictionCartReturn {
  journeyAnalysis: {
    payloads: JourneyPredictionPayload[];
    hasIncompleteJourneys: boolean;
    hasNoJourneys: boolean;
  };
  data: JourneyPredictionCartResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: any;
  lastFetchedTime: Date | null;
  timeElapsed: number;
  formattedTimeElapsed: string;
  currentTime: Date;
  refetch: () => void;
}

export const usePredictionCart = (): UsePredictionCartReturn => {
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const journeys = useAppSelector(selectJourneys);
  const journeyEvents = useAppSelector(selectEvents);
  const selectedOrderId = selectedOrder?.id || "";
  const orderTime = selectedOrder?.time || "";
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastFetchedTime, setLastFetchedTime] = useState<Date | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const journeyAnalysis = useMemo(() => {
    if (!selectedOrderId) {
      return {
        payloads: [],
        hasIncompleteJourneys: false,
        hasNoJourneys: false,
      };
    }

    const payloads: JourneyPredictionPayload[] = [];
    let hasIncompleteJourneys = false;
    const hasNoJourneys = journeys?.length === 0;

    if (hasNoJourneys) {
      hasIncompleteJourneys = true;
    } else {
      journeys.forEach((journey) => {
        if (journey.status === ANNOTATION_RECORD_STATUSES.DELETED) {
          return;
        }

        const enterEvent = journeyEvents.find(
          (event) =>
            event.journeyId === journey.id && event.eventType === "enter"
        );
        const exitEvent = journeyEvents.find(
          (event) =>
            event.journeyId === journey.id && event.eventType === "exit"
        );

        if (enterEvent && exitEvent) {
          const payload = createJourneyPredictionPayload({
            enterEvent,
            exitEvent,
          });
          payloads.push(payload);
        } else {
          hasIncompleteJourneys = true;
        }
      });
    }

    return { payloads, hasIncompleteJourneys, hasNoJourneys };
  }, [selectedOrderId, journeys, journeyEvents]);

  const {
    data,
    refetch: refetchPrediction,
    isFetching,
    isLoading,
    error,
  } = useGetJourneyPredictionCartQuery(
    {
      orderTime,
      journeys: journeyAnalysis.payloads,
    },
    {
      skip:
        !orderTime ||
        journeyAnalysis.payloads.length === 0 ||
        journeyAnalysis.hasIncompleteJourneys,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (data && !isFetching) {
      setLastFetchedTime(new Date());
    }
  }, [data, isFetching]);

  const timeElapsed = useMemo(() => {
    if (isLoading || !lastFetchedTime) {
      return 0;
    }
    return currentTime.getTime() - lastFetchedTime.getTime();
  }, [isLoading, lastFetchedTime, currentTime]);

  const formattedTimeElapsed = useMemo(() => {
    if (isLoading || !lastFetchedTime) {
      return "-";
    }
    return formatTimeDifference(timeElapsed);
  }, [isLoading, lastFetchedTime, timeElapsed]);

  const refetch = useCallback(() => {
    if (journeyAnalysis.hasIncompleteJourneys) {
      toast.error("Complete all journey annotations, then try again");
      return;
    }
    refetchPrediction();
  }, [journeyAnalysis.hasIncompleteJourneys, refetchPrediction]);

  return {
    journeyAnalysis,
    data,
    isLoading,
    isFetching,
    error,
    lastFetchedTime,
    timeElapsed,
    formattedTimeElapsed,
    currentTime,
    refetch,
  };
};
